import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/button";
import { LanguageSwitcher } from "./LanguageSwitcher";

export const Navbar = () => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    const NAV_LINKS = [
        { key: "process", href: "/#process" },
        { key: "specialities", href: "/#specialities" },
        { key: "doctors", href: "/#doctors" },
        { key: "hospitals", href: "/#hospitals" },
        { key: "stories", href: "/#testimonials" },
        { key: "enquiry", href: "/#enquiry" },
    ];

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 10);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-white/75 backdrop-blur-xl border-b border-black/5 shadow-[0_2px_20px_-12px_rgba(0,0,0,0.15)]"
                    : "bg-transparent"
            }`}
        >
            <div className="mx-auto max-w-7xl px-5 sm:px-8 h-24 md:h-28 flex items-center justify-between overflow-visible">
                {/* Logo */}
                <a
                    href="/"
                    data-testid="navbar-logo"
                    className="flex items-center group relative"
                >
                    <img
                        src="https://res.cloudinary.com/deq3rnrha/image/upload/v1777362730/57F66F69-6741-41CE-9E1B-06B8AB12B13D_izmfua.png"
                        alt="TruHeal India"
                        className="h-48 md:h-56 w-auto group-hover:scale-105 transition-transform"
                    />
                </a>

                {/* Desktop menu */}
                <nav className="hidden lg:flex items-center gap-8">
                    {NAV_LINKS.map((l) => (
                        <a
                            key={l.key}
                            href={l.href}
                            data-testid={`nav-link-${l.key}`}
                            className="text-sm font-medium text-stone-700 hover:text-brand-700 transition-colors relative after:content-[''] after:absolute after:left-0 after:bottom-[-6px] after:h-[2px] after:w-0 hover:after:w-full after:bg-brand-700 after:transition-all"
                        >
                            {t(`nav.${l.key}`)}
                        </a>
                    ))}
                    <LanguageSwitcher />
                    <Button
                        asChild
                        data-testid="nav-cta-enquire"
                        className="rounded-full bg-orange-500 hover:bg-orange-600 text-white px-5 shadow-md hover:-translate-y-0.5 transition-all"
                    >
                        <a href="/#enquiry">{t("nav.cta")}</a>
                    </Button>
                </nav>

                {/* Mobile actions */}
                <div className="lg:hidden flex items-center gap-2">
                    <LanguageSwitcher />
                    <button
                        data-testid="navbar-mobile-toggle"
                        onClick={() => setOpen((v) => !v)}
                        className="p-2 rounded-md bg-white/80 border border-stone-200"
                        aria-label="Toggle menu"
                    >
                        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
            </div>

            {/* Mobile menu */}
            {open && (
                <div
                    data-testid="navbar-mobile-menu"
                    className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-stone-200 px-5 py-4"
                >
                    <div className="flex flex-col gap-1">
                        {NAV_LINKS.map((l) => (
                            <a
                                key={l.key}
                                href={l.href}
                                onClick={() => setOpen(false)}
                                className="py-2.5 text-stone-800 font-medium border-b border-stone-100 last:border-0"
                                data-testid={`mobile-nav-link-${l.key}`}
                            >
                                {t(`nav.${l.key}`)}
                            </a>
                        ))}
                        <Button
                            asChild
                            className="mt-3 rounded-full bg-orange-500 hover:bg-orange-600 text-white"
                            data-testid="mobile-nav-cta-enquire"
                        >
                            <a href="/#enquiry" onClick={() => setOpen(false)}>
                                {t("nav.cta")}
                            </a>
                        </Button>
                    </div>
                </div>
            )}
        </header>
    );
};
